import { FlashFilled16 } from '@carbon/icons-react';
import { Tooltip } from 'antd';
import clsx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { useTranslation } from 'hooks/use-translation';
import { queueMacrotask } from 'utils/macrotask';

const FooterWrapper = styled.div`
  position: fixed;
  z-index: 9999999;
  left: 20px;
  bottom: 50px;
`;

const Link = styled.a`
  color: var(--cp-link-primary);

  .cp-footer-light-background & {
    color: var(--cp-link-inverse);
  }
`;

const FooterCard = styled.div`
  position: fixed;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.95);
  color: var(--cp-black);
  box-shadow: 0px 3.6px 8.1px 0px rgba(0, 0, 0, 0.13),
    0px 0.675px 2.025px 0px rgba(0, 0, 0, 0.11);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--cp-spacing-02);
  height: 2rem;
  padding: var(--cp-spacing-03);
  font-size: var(--font-size-base);

  .cp-footer-light-background & {
    background: var(--cp-background-inverse);
    color: var(--cp-white);
  }
`;

export const CpFooter: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const callbackRef = useRef<VoidFunction>(() => {});
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);

  useEffect(() => {
    callbackRef.current = () => {
      // get the bottom most element of class .rl-cp-section in the viewport
      // NOTE: the element should be in the viewport not outside the viewport
      const sections = document.querySelectorAll('.rl-cp-section');

      let currentSection: null | Element = null;

      // Find the section currently under the footer
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();

        if (rect.top <= window.innerHeight && rect.bottom >= 0) {
          currentSection = section;
        }

        const sectionId = section.parentElement?.getAttribute?.('id');

        if (currentSectionId === sectionId) {
          return;
        }

        if (currentSection) {
          setCurrentSectionId(currentSection.id);
        }

        if (currentSection) {
          const classNames = currentSection.classList;

          if (classNames.contains('cp-dark-background')) {
            setIsDarkMode(true);
          } else {
            setIsDarkMode(false);
          }
        }
      });
    };

    const wrapper = document.querySelector('.rl-cp-top-scroll-wrapper');

    queueMacrotask(() => {
      callbackRef.current();
    });

    window.addEventListener('scroll', callbackRef.current);
    if (wrapper) {
      wrapper.addEventListener('scroll', callbackRef.current);
    }

    return () => {
      window.removeEventListener('scroll', callbackRef.current);
      if (wrapper) {
        wrapper.addEventListener('scroll', callbackRef.current);
      }
    };
  }, [currentSectionId]);

  const cls = clsx('cp-footer', {
    'cp-footer-dark-background': isDarkMode,
    'cp-footer-light-background': !isDarkMode,
  });

  return (
    <FooterWrapper className={cls}>
      <Tooltip title={useTranslation('cp_footer.tooltip')}>
        <FooterCard>
          <FlashFilled16 fill="#FF832B" />
          <FormattedMessage id="by" />
          <Link href="https://rocketlane.com" target="_blank" rel="noreferrer">
            <FormattedMessage id="rocketlane" />
          </Link>
        </FooterCard>
      </Tooltip>
    </FooterWrapper>
  );
};
